import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { ExternalLink, Users, Bookmark, Check } from 'lucide-react'
import { instagramService } from '@/services/instagram-service'
import { useToast } from '@/hooks/use-toast'

interface SearchResultCardProps {
  title: string
  snippet: string
  followers: number
  url: string
}

export function SearchResultCard({ title, snippet, followers, url }: SearchResultCardProps) {
  const [isSaving, setIsSaving] = useState(false)
  const [isSaved, setIsSaved] = useState(false)
  const { toast } = useToast()

  const formatFollowers = (count: number) => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`
    return count.toString()
  }

  const cleanTitle = (title: string) => {
    let cleaned = title
    // Remove each word separately and robustly
    cleaned = cleaned.replace(/•/g, '') // Remove bullet points
    cleaned = cleaned.replace(/\binstagram\b/gi, '') // Remove "Instagram" as whole word
    cleaned = cleaned.replace(/\bphotos\b/gi, '') // Remove "photos" as whole word
    cleaned = cleaned.replace(/\bvideos\b/gi, '') // Remove "videos" as whole word
    cleaned = cleaned.replace(/\band\b/gi, '') // Remove "and" as whole word
    cleaned = cleaned.replace(/\.\.\./g, '') // Remove ellipsis (...)
    cleaned = cleaned.replace(/\s+/g, ' ').trim()
    return cleaned
  }

  const trimSnippet = (text: string, maxLength: number = 120) => {
    if (text.length <= maxLength) return text
    return text.substring(0, maxLength).trim() + '...'
  }

  const handleSave = async () => {
    if (isSaved || isSaving) return

    setIsSaving(true)
    try {
      await instagramService.saveInfluencer({
        title: cleanTitle(title),
        url,
        snippet,
        followers
      })
      
      setIsSaved(true)
      toast({
        title: "Profile Saved",
        description: `${cleanTitle(title)} has been saved to your collection.`,
      })
    } catch (error) {
      console.error('Failed to save influencer:', error)
      toast({
        title: "Save Failed",
        description: "Failed to save the profile. Please try again.",
        variant: "destructive",
      })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <TooltipProvider>
        <Card className="group hover:shadow-md transition-all duration-200 border border-blue-200/20 dark:border-blue-800/20">
          <CardHeader className="pb-3">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-blue-50/50 dark:bg-blue-950/20 flex-shrink-0">
                <Users className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1 min-w-0">
                <CardTitle className="text-base font-semibold group-hover:text-primary transition-colors">
                  {cleanTitle(title)}
                </CardTitle>
                <div className="flex items-center gap-1 mt-1 text-sm text-muted-foreground">
                  <Users className="h-3 w-3" />
                  <span className="font-medium">{formatFollowers(followers)} followers</span>
                </div>
              </div>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed mt-2">
              {trimSnippet(snippet)}
            </p>
          </CardHeader>
          
          <CardContent className="pt-0">
            <div className="flex gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button asChild size="sm" variant="outline" className="flex-1">
                    <a href={url} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="mr-2 h-3 w-3" />
                      View
                    </a>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>View Instagram profile</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    size="sm" 
                    variant={isSaved ? "default" : "outline"} 
                    className="flex-1"
                    onClick={handleSave}
                    disabled={isSaving || isSaved}
                  >
                    {isSaved ? (
                      <>
                        <Check className="mr-2 h-3 w-3" />
                        Saved
                      </>
                    ) : (
                      <>
                        <Bookmark className="mr-2 h-3 w-3" />
                        {isSaving ? 'Saving...' : 'Save'}
                      </>
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{isSaved ? 'Profile saved' : 'Save to your collection'}</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </CardContent>
        </Card>
    </TooltipProvider>
  )
} 